import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

export interface ChannelListType {
  channelName: string;
  channelType: string;
  configId: string;
  createdAt: string;
  id: string;
  teamId: string | null;
  tenantId: string;
  updatedAt: string;
}

export const chatApi = createApi({
  reducerPath: 'chatApi',
  baseQuery,
  endpoints: (builder) => ({
    filterConversation: builder.mutation({
      query: (filterQuery) => ({
        url: `conversation/list`,
        method: 'POST',
        body: filterQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { conversation?: any } }) => ({
        list: response?.data?.conversation?.list ?? [],
        totalPages: response?.data?.conversation?.totalPages ?? 0,
      }),
    }),
    createConversation: builder.mutation({
      query: (newConversation) => ({
        url: 'conversation/add',
        method: 'POST',
        body: newConversation,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    updateConversation: builder.mutation({
      query: (updateConversation) => ({
        url: 'conversation/update',
        method: 'POST',
        body: updateConversation,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    conversationDetails: builder.mutation({
      query: (updateConversation) => ({
        url: 'conversation/details',
        method: 'POST',
        body: updateConversation,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    deleteConversation: builder.mutation({
      query: (ids: string[]) => ({
        url: 'conversation/delete',
        method: 'POST',
        body: { ids },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getMessageList: builder.mutation({
      query: (messageFilter) => ({
        url: 'message/list',
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { message?: any } }) => ({
        list: response?.data?.message?.list ?? [],
        totalPages: response?.data?.message?.totalPages ?? 0,
        total: response?.data?.message?.total ?? 0,
      }),
    }),
    updateMessageMarkAsRead: builder.mutation({
      query: (messageFilter) => ({
        url: 'message/markAsRead',
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { message?: any } }) => ({
        list: response?.data?.message?.list ?? [],
        totalPages: response?.data?.message?.totalPages ?? 0,
      }),
    }),
    sendMessage: builder.mutation({
      query: (messageDetails) => ({
        url: 'message/send',
        method: 'POST',
        body: messageDetails,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getMessageDetails: builder.mutation({
      query: (messageDetails) => ({
        url: 'message/details',
        method: 'POST',
        body: messageDetails,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getMediaUrl: builder.mutation({
      query: (fileName) => ({
        url: 'gcstorage/getPresignedUrl',
        method: 'POST',
        body: fileName,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? '',
    }),
    putPresignedMediaUrl: builder.mutation({
      query: (fileName) => ({
        url: 'gcstorage/putPresignedUrl',
        method: 'POST',
        body: fileName,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? '',
    }),
    getCallList: builder.mutation({
      query: (messageFilter) => ({
        url: 'call/list',
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { call?: any } }) => ({
        list: response?.data?.call?.list ?? [],
        totalPages: response?.data?.call?.totalPages ?? 0,
        total: response?.data?.call?.total,
      }),
    }),
    getCallDetails: builder.mutation({
      query: (messageFilter) => ({
        url: 'call/details',
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: any }) => response?.data?.call ?? '',
    }),
    getCallStreamRecording: builder.mutation<string | { error: string }, { id: string }>({
      query: (messageFilter) => ({
        url: 'call/streamRecording',
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
        responseHandler: async (response) => {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            return response.json();
          }
          return response.blob();
        },
      }),
      transformResponse: async (data: Blob | { error: string }) => {
        if ('error' in data) {
          return data;
        }

        const reader = new FileReader();
        return new Promise<string>((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(data);
        });
      },
    }),
    exportcall: builder.mutation({
      query: (filter) => ({
        url: 'call/export',
        method: 'POST',
        body: filter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getEmailList: builder.mutation({
      query: (messageFilter) => ({
        url: 'email/list',
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { message?: any } }) => ({
        list: response?.data?.message?.list ?? [],
        totalPages: response?.data?.message?.totalPages ?? 0,
      }),
    }),
    sendEmail: builder.mutation({
      query: (emailDetails) => ({
        url: 'email/send',
        method: 'POST',
        body: emailDetails,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    syncCallLoglist: builder.mutation({
      query: (emailDetails) => ({
        url: 'call/syncTenantCDR',
        method: 'POST',
        body: emailDetails,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),

  // updateAccount: builder.mutation<IAccount, { id: string; updates: Partial<IAccount> }>({
  //   query: ({ id, updates }) => ({
  //     url: `company/update`,
  //     method: 'POST',
  //     body: { id, ...updates },
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //   }),
  // }),
  // deleteAccount: builder.mutation<{ success: boolean; id: string }, { id: string }>({
  //   query: ({ id }) => ({
  //     url: `company/delete/${id}`,
  //     method: 'DELETE',
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //   }),
  //   transformResponse: (response: { success: boolean }, meta, arg) => ({
  //     success: response.success,
  //     id: arg.id,
  //   }),
  // }),
});

export const {
  useFilterConversationMutation,
  useCreateConversationMutation,
  useConversationDetailsMutation,
  useGetMessageListMutation,
  useSendMessageMutation,
  useGetMediaUrlMutation,
  usePutPresignedMediaUrlMutation,
  useGetCallListMutation,
  useExportcallMutation,
  useUpdateConversationMutation,
  useGetMessageDetailsMutation,
  useGetCallDetailsMutation,
  useGetCallStreamRecordingMutation,
  useUpdateMessageMarkAsReadMutation,
  useGetEmailListMutation,
  useSendEmailMutation,
  useDeleteConversationMutation,
  useSyncCallLoglistMutation,
  // useCreateAccountMutation,
  // useUpdateAccountMutation,
  // useDeleteAccountMutation,
} = chatApi;
